/**
 * Submission files store (R3 T6a/T6b, plan §3.7).
 *
 * One read per bid — `GET /bids/{id}/submission-files` — gives the Submit
 * page each checklist item's file with its checks, and the lodgement records.
 * The Mark-as-submitted dialog reads the same state, so the files it offers to
 * freeze are exactly the ones the page shows.
 *
 * Like the Pack store this is a read model: every write re-reads, because the
 * file an item sends and its checks are decided server-side.
 */

import { create } from 'zustand'
import {
  downloadSelectedSubmissionFiles,
  getSubmissionFiles,
  lodgeSubmission,
  setSubmissionFile,
} from '@/lib/api'
import type {
  LodgeSubmissionInput,
  SubmissionFilesResponse,
  SubmissionRecord,
} from '@/types/submission-files'

interface SubmissionFilesState {
  /** Bid the current state belongs to (stores are global). */
  bidId: string | null
  data: SubmissionFilesResponse | null
  isLoading: boolean
  error: string | null
  /** True from the ZIP request until the download fires (or fails). */
  isDownloading: boolean
  isLodging: boolean
  /** When `data` was last read (ms). Download links in it live 15 minutes. */
  loadedAt: number | null

  /** Load a bid's files. Another bid's state is dropped first. */
  load: (bidId: string) => Promise<void>
  /** Silent re-read of the current bid — no spinner, keeps the last good data on failure. */
  refresh: () => Promise<void>
  /** Re-read when the download links may have expired; resolves with the current data. */
  ensureFresh: () => Promise<SubmissionFilesResponse | null>
  /** Pin a version as an item's file; null goes back to the newest. */
  pin: (itemId: string, outputVersionId: string | null) => Promise<void>
  /** ZIP of the selected files. Never marks the bid lodged. */
  downloadAll: () => Promise<void>
  /** Freeze copies of the chosen files. Does not move the bid's status. */
  lodge: (bidId: string, input: LodgeSubmissionInput) => Promise<SubmissionRecord>
  reset: () => void
}

/** Links are signed for 15 minutes; re-read a little before that. */
const LINK_REFRESH_AFTER_MS = 10 * 60_000

/**
 * The section's own message when the list can't load. The raw error is never
 * shown: the app's generic network text names API URLs and env settings.
 */
export const FILES_LOAD_ERROR = "We couldn't load the files to send. Try again."

export const useSubmissionFilesStore = create<SubmissionFilesState>((set, get) => ({
  bidId: null,
  data: null,
  isLoading: false,
  error: null,
  isDownloading: false,
  isLodging: false,
  loadedAt: null,

  load: async (bidId) => {
    if (get().bidId !== bidId) set({ bidId, data: null, error: null })
    set({ isLoading: true })
    try {
      const data = await getSubmissionFiles(bidId)
      if (get().bidId === bidId) set({ data, isLoading: false, error: null, loadedAt: Date.now() })
    } catch {
      if (get().bidId === bidId) set({ isLoading: false, error: FILES_LOAD_ERROR })
    }
  },

  refresh: async () => {
    const bidId = get().bidId
    if (!bidId) return
    try {
      const data = await getSubmissionFiles(bidId)
      if (get().bidId === bidId) set({ data, error: null, loadedAt: Date.now() })
    } catch {
      // Keep the last good data on screen; the next action re-reads.
    }
  },

  ensureFresh: async () => {
    const { loadedAt } = get()
    if (loadedAt === null || Date.now() - loadedAt > LINK_REFRESH_AFTER_MS) await get().refresh()
    return get().data
  },

  pin: async (itemId, outputVersionId) => {
    const bidId = get().bidId
    if (!bidId) throw new Error('No bid loaded')
    await setSubmissionFile(bidId, itemId, outputVersionId)
    await get().refresh()
  },

  downloadAll: async () => {
    const bidId = get().bidId
    if (!bidId) throw new Error('No bid loaded')
    set({ isDownloading: true })
    try {
      await downloadSelectedSubmissionFiles(bidId)
    } finally {
      set({ isDownloading: false })
    }
  },

  lodge: async (bidId, input) => {
    set({ isLodging: true })
    try {
      const record = await lodgeSubmission(bidId, input)
      if (get().bidId === bidId) await get().refresh()
      return record
    } finally {
      set({ isLodging: false })
    }
  },

  reset: () =>
    set({ bidId: null, data: null, isLoading: false, error: null, isDownloading: false, isLodging: false, loadedAt: null }),
}))
